import React from "react";
import { ExpandableCard, Typography, Tag, CircularProgress, WidgetEmptyErrorPlaceholder } from "@ann/web-components";
import { useVehicleOpportunities } from "../../hooks/useVehicleOpportunities";
import {
  ContentWrapper,
  RowWrapper,
  ColumnWrapper,
  BodyContainer,
  InlineTagWrapper,
  StatusWrapper,
} from "./OpportunitiesContent.styled";

const getCategoryColor = (category) => {
  if (!category) return "neutral";
  
  switch (category.toUpperCase()) {
    case "MAINTENANCE":
      return "warning";
    case "SERVICE":
      return "informative";
    case "WARRANTY":
      return "alert";
    case "CONTRACT_END":
      return "alert";
    case "IN_MARKET":
      return "informative";
    default:
      return "neutral";
  }
};

const formatCategoryLabel = (category) => {
  if (!category) return "Unknown";
  return category
    .split("_")
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

const OpportunitiesContent = ({ partyUuid, vehicleUuid }) => {
  const { opportunities, loading, error, isEmpty, refetch } = useVehicleOpportunities(partyUuid, vehicleUuid);

  if (loading) {
    return (
      <ContentWrapper>
        <CircularProgress size="medium" />
      </ContentWrapper>
    );
  }

  if (error) {
    return (
      <WidgetEmptyErrorPlaceholder
        buttonText="Retry"
        icon="error"
        message="We couldn't load your Opportunities. Please try again."
        onClick={refetch}
        showButton
        title="Something went wrong"
      />
    );
  }

  if (isEmpty || !opportunities.length) {
    return (
      <WidgetEmptyErrorPlaceholder
        icon="trendingUp"
        message="You'll see client activity here once there's engagement."
        showButton={false}
        title="No active opportunities"
      />
    );
  }

  return (
    <ContentWrapper>
      {opportunities.map((opportunity, index) => (
        <ExpandableCard key={opportunity.id || `opportunity-${index}`}>
          <ExpandableCard.Header>
            <RowWrapper>
              <ColumnWrapper className="col-1">
                <Typography category="subtitle" weight="bold" size="small">
                  {opportunity.alert_title || "Opportunity"}
                </Typography>
                <Typography category="body" weight="regular" size="medium" color="body">
                  {opportunity.description || "No description available"}
                </Typography>
                {opportunity.created_at && (
                  <Typography category="body" weight="regular" size="small" color="body">
                    Created: {new Date(opportunity.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </Typography>
                )}
              </ColumnWrapper>
              <ColumnWrapper className="col-2">
                <StatusWrapper>
                  <Typography category="subtitle" weight="bold" size="small">
                    Category
                  </Typography>
                </StatusWrapper>
                <InlineTagWrapper>
                  <Tag
                    color={getCategoryColor(opportunity.category)}
                    size="small"
                    text={formatCategoryLabel(opportunity.category)}
                  />
                </InlineTagWrapper>
              </ColumnWrapper>
            </RowWrapper>
          </ExpandableCard.Header>
          <ExpandableCard.Content>
            <BodyContainer>
              {opportunity.description && (
                <Typography category="body" weight="regular" size="medium" color="body">
                  {opportunity.description}
                </Typography>
              )}
            </BodyContainer>
          </ExpandableCard.Content>
        </ExpandableCard>
      ))}
    </ContentWrapper>
  );
};

export default OpportunitiesContent;
